import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

export function useEntities(page = 1, pageSize = 100, search = '', advisorId = null) {
  const [entities, setEntities] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchEntities()
  }, [page, search, advisorId])

  const fetchEntities = async () => {
    try {
      setLoading(true)
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      let query = supabase
        .from('entities')
        .select('*, entity_addresses(*), entity_contacts(*), entity_banking_details(*)', { count: 'exact' })

      if (search) {
        query = query.or(`name.ilike.%${search}%,entity_num.ilike.%${search}%,customer_code.ilike.%${search}%`)
      }

      if (advisorId) {
        query = query.eq('advisor_id', advisorId)
      }

      const { data, count, error: err } = await query
        .order('created_at', { ascending: false })
        .range(from, to)

      if (err) throw err
      setEntities(data || [])
      setTotal(count || 0)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const _saveRelated = async (entityId, addressData, contactData, bankingData) => {
    // Address
    const hasAddress = addressData && Object.values(addressData).some(v => v && v !== 'South Africa')
    await supabase.from('entity_addresses').delete().eq('entity_id', entityId)
    if (hasAddress) {
      await supabase.from('entity_addresses').insert([{ ...addressData, entity_id: entityId }])
    }

    // Contact
    const hasContact = contactData && Object.values(contactData).some(v => v)
    await supabase.from('entity_contacts').delete().eq('entity_id', entityId)
    if (hasContact) {
      await supabase.from('entity_contacts').insert([{ ...contactData, entity_id: entityId }])
    }

    // Banking
    const { active: _ba, ...bankingFields } = bankingData || {}
    const hasBanking = bankingData && Object.values(bankingFields).some(v => v)
    await supabase.from('entity_banking_details').delete().eq('entity_id', entityId)
    if (hasBanking) {
      await supabase.from('entity_banking_details').insert([{ ...bankingData, entity_id: entityId }])
    }
  }

  const createEntity = async (entityData, addressData, contactData, bankingData) => {
    const { data, error } = await supabase
      .from('entities')
      .insert([entityData])
      .select()
    if (error) throw error
    const entity = data[0]
    await _saveRelated(entity.id, addressData, contactData, bankingData)
    await fetchEntities()
    return entity
  }

  const updateEntity = async (id, entityData, addressData, contactData, bankingData) => {
    const { data, error } = await supabase
      .from('entities')
      .update(entityData)
      .eq('id', id)
      .select()
    if (error) throw error
    await _saveRelated(id, addressData, contactData, bankingData)
    await fetchEntities()
    return data[0]
  }

  const deleteEntity = async (id) => {
    const { error } = await supabase
      .from('entities')
      .delete()
      .eq('id', id)
    if (error) throw error
    await fetchEntities()
  }

  // Import a list of { entityData, addressData, contactData, bankingData } objects.
  // Upserts: matches by customer_code (preferred) or name, updates if found, inserts if new.
  // Returns { created, updated }.
  const importEntities = async (rows) => {
    // Load all existing entities for matching
    const { data: allExisting } = await supabase
      .from('entities')
      .select('id, name, customer_code')

    const byCode = {}
    const byName = {}
    ;(allExisting || []).forEach(e => {
      if (e.customer_code) byCode[e.customer_code.toLowerCase()] = e
      if (e.name)          byName[e.name.toLowerCase()] = e
    })

    // Convert empty strings to null so they don't violate UNIQUE constraints
    const clean = (obj) => Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v])
    )

    let created = 0, updated = 0
    for (const { entityData, addressData, contactData, bankingData } of rows) {
      const cleanEntity = clean(entityData)

      // Match priority: customer_code → name
      const existing =
        (cleanEntity.customer_code && byCode[cleanEntity.customer_code.toLowerCase()]) ||
        (cleanEntity.name && byName[cleanEntity.name.toLowerCase()])

      if (existing) {
        const { error } = await supabase.from('entities').update(cleanEntity).eq('id', existing.id)
        if (error) throw error
        await _saveRelated(existing.id, addressData, contactData, bankingData)
        updated++
      } else {
        const { data, error } = await supabase.from('entities').insert([cleanEntity]).select('id').single()
        if (error) throw error
        await _saveRelated(data.id, addressData, contactData, bankingData)
        created++
      }
    }

    await fetchEntities()
    return { created, updated }
  }

  // Find entities by name, auto-creating stubs for any that don't exist yet.
  // Returns a map of { name → id }.
  const resolveEntityNames = async (names) => {
    const unique = [...new Set(names.filter(Boolean))]
    if (!unique.length) return {}

    const { data: existing } = await supabase
      .from('entities')
      .select('id, name')
      .in('name', unique)

    const nameToId = {}
    ;(existing || []).forEach(e => { nameToId[e.name] = e.id })

    const missing = unique.filter(n => !nameToId[n])
    if (missing.length > 0) {
      const { data: created } = await supabase
        .from('entities')
        .insert(missing.map(n => ({ name: n, active: true })))
        .select('id, name')
      ;(created || []).forEach(e => { nameToId[e.name] = e.id })
    }

    return nameToId
  }

  // Relink policies to entities by matching customer_code.
  // Returns count of successfully relinked policies.
  const relinkPoliciesByCode = async () => {
    const { data: allEntities, error } = await supabase
      .from('entities')
      .select('id, customer_code')
      .not('customer_code', 'is', null)
    if (error) throw error

    const BATCH = 20
    let relinked = 0
    for (let i = 0; i < allEntities.length; i += BATCH) {
      const results = await Promise.all(
        allEntities.slice(i, i + BATCH).map(e =>
          supabase.from('policies')
            .update({ entity_id: e.id })
            .eq('customer_code', e.customer_code)
        )
      )
      relinked += results.filter(r => !r.error).reduce((sum, r) => sum + (r.data?.count || 0), 0)
    }
    return relinked
  }

  return {
    entities,
    total,
    loading,
    error,
    createEntity,
    updateEntity,
    deleteEntity,
    importEntities,
    resolveEntityNames,
    relinkPoliciesByCode,
    refetch: fetchEntities
  }
}
